import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchDashboardV2Financial,
  fetchDashboardV2Operations,
  fetchDashboardV2Overview,
  fetchDashboardV2Players,
  fetchDashboardV2Roadblocks,
  type DashboardV2Period,
  type DashboardV2PeriodParams,
  type ShopDashboardV2FinancialData,
  type ShopDashboardV2OperationsData,
  type ShopDashboardV2OverviewData,
  type ShopDashboardV2PlayersData,
  type ShopDashboardV2RoadblocksData,
} from '../services/api';

type QueryState<T> = {
  data: T | null;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  refreshing: boolean;
  refetch: () => Promise<void>;
};

type Fetcher<T> = (
  period: DashboardV2Period,
  refresh?: boolean,
  opts?: { tz?: string; dateFrom?: string; dateTo?: string },
) => Promise<T>;

function useDashboardV2Query<T>(
  fetcher: Fetcher<T>,
  params: DashboardV2PeriodParams,
  enabled = true,
): QueryState<T> {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(enabled);
  const [isFetching, setIsFetching] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const requestIdRef = useRef(0);
  const optsKey = JSON.stringify({
    tz: params.tz ?? null,
    dateFrom: params.period === 'custom' ? params.dateFrom ?? null : null,
    dateTo: params.period === 'custom' ? params.dateTo ?? null : null,
  });

  const load = useCallback(
    async (refresh = false, silent = false) => {
      if (!enabled) {
        setIsLoading(false);
        setIsFetching(false);
        setRefreshing(false);
        return;
      }
      const requestId = ++requestIdRef.current;
      if (refresh) setRefreshing(true);
      else if (!silent) setIsLoading(true);
      setIsFetching(true);
      setError(null);
      try {
        const opts = JSON.parse(optsKey) as {
          tz: string | null;
          dateFrom: string | null;
          dateTo: string | null;
        };
        const result = await fetcherRef.current(params.period, refresh, {
          tz: opts.tz || undefined,
          dateFrom: opts.dateFrom || undefined,
          dateTo: opts.dateTo || undefined,
        });
        if (requestId !== requestIdRef.current) return;
        setData(result);
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        const e = err instanceof Error ? err : new Error('Failed to load dashboard');
        setError(e);
      } finally {
        if (requestId === requestIdRef.current) {
          setIsLoading(false);
          setIsFetching(false);
          setRefreshing(false);
        }
      }
    },
    [enabled, params.period, optsKey],
  );

  useEffect(() => {
    void load(false);
  }, [load]);

  const refresh = useCallback(async () => load(true, true), [load]);
  const refetch = useCallback(async () => load(false, true), [load]);

  return { data, isLoading, isFetching, error, refresh, refreshing, refetch };
}

export const useDashboardV2Overview = (params: DashboardV2PeriodParams, enabled = true) =>
  useDashboardV2Query<ShopDashboardV2OverviewData>(
    fetchDashboardV2Overview,
    params,
    enabled && (params.period !== 'custom' || Boolean(params.dateFrom && params.dateTo)),
  );

export const useDashboardV2Financial = (params: DashboardV2PeriodParams, enabled = true) =>
  useDashboardV2Query<ShopDashboardV2FinancialData>(
    fetchDashboardV2Financial,
    params,
    enabled && (params.period !== 'custom' || Boolean(params.dateFrom && params.dateTo)),
  );

export const useDashboardV2Players = (params: DashboardV2PeriodParams, enabled = true) =>
  useDashboardV2Query<ShopDashboardV2PlayersData>(
    fetchDashboardV2Players,
    params,
    enabled && (params.period !== 'custom' || Boolean(params.dateFrom && params.dateTo)),
  );

export const useDashboardV2Operations = (params: DashboardV2PeriodParams, enabled = true) =>
  useDashboardV2Query<ShopDashboardV2OperationsData>(
    fetchDashboardV2Operations,
    params,
    enabled && (params.period !== 'custom' || Boolean(params.dateFrom && params.dateTo)),
  );

export const useDashboardV2Roadblocks = (params: DashboardV2PeriodParams, enabled = true) =>
  useDashboardV2Query<ShopDashboardV2RoadblocksData>(
    fetchDashboardV2Roadblocks,
    params,
    enabled && (params.period !== 'custom' || Boolean(params.dateFrom && params.dateTo)),
  );
